import React, { useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import { setCurrentScreenName } from './services/api';
import { getBasePath } from './utils/basePath';
import Dashboard from './pages/Dashboard';
import TenantAdoption from './pages/TenantAdoption';
import CardMetrics from './pages/CardMetrics';
import ConsumerQueries from './pages/ConsumerQueries';
import CallCenterDashboard from './pages/CallCenterDashboard';
import DailyCallCenterHealthSummary from './pages/DailyCallCenterHealthSummary';
import AgentProductivityDaily from './pages/AgentProductivityDaily';
import CallIntentPatterns from './pages/CallIntentPatterns';
import CasePatternAnalysis from './pages/CasePatternAnalysis';
import Reports from './pages/Reports';
import Analytics from './pages/Analytics';
import CustomQuery from './pages/CustomQuery';
import FISCard30 from './pages/FISCard30';
import FISCard35 from './pages/FISCard35';
import EnrollmentChecklist from './pages/EnrollmentChecklist';
import CardTransactionPatterns from './pages/CardTransactionPatterns';
import TechnologyDashboard from './pages/technology/TechnologyDashboard';
import SprintTeamPage from './pages/technology/SprintTeamPage';
import KanbanTeamPage from './pages/technology/KanbanTeamPage';
import SprintGoals from './pages/technology/SprintGoals';
import ReleaseReadiness from './pages/technology/ReleaseReadiness';
import TechSupport from './pages/TechSupport';
import LogPatterns from './pages/LogPatterns';
import HealthMetrics from './pages/HealthMetrics';
import UserManagement from './pages/admin/UserManagement';
import RoleManagement from './pages/admin/RoleManagement';
import AuditLogs from './pages/admin/AuditLogs';
import LiveCalls from './pages/operations/LiveCalls';
import PrintOpsHealth from './pages/operations/PrintOpsHealth';
import PrintOpsFileTracker from './pages/operations/PrintOpsFileTracker';
import EligibilityCheck from './pages/dataops/EligibilityCheck';
import EligibilityData from './pages/dataops/EligibilityData';
import FileIntegrity from './pages/dataops/FileIntegrity';
import PrefundingReport from './pages/dataops/PrefundingReport';
import RewardIntegrityChecks from './pages/dataops/RewardIntegrityChecks';
import AplLookup from './pages/FIS/AplLookup';
import MerchantNetworkLookup from './pages/FIS/MerchantNetworkLookup';
import ConsumerFinancialTransactions from './pages/ConsumerFinancialTransactions';
import ReconciliationReport from './pages/ReconciliationReport';
import CardStatusHistory from './pages/CardStatusHistory';
// ADM-1009 prototype — remove on cleanup / merge decline
import CardOrderingAudit from './pages/CardOrderingAudit';
import ReconRules from './pages/recon/ReconRules';
import ReconRulesets from './pages/recon/ReconRulesets';
import ReconProcesses from './pages/recon/ReconProcesses';
import ReconDashboard from './pages/recon/ReconDashboard';
import MasterActionCatalog from './pages/program-design/MasterActionCatalog';
import AccConfigBuilder from './pages/program-design/AccConfigBuilder';
import AccGuidedBuilder from './pages/program-design/AccGuidedBuilder';
import AccWizard from './pages/program-design/AccWizard';
import AccLearn from './pages/program-design/AccLearn';
import ServiceCatalog from './pages/registry/ServiceCatalog';
import ServiceDetail from './pages/registry/ServiceDetail';
import DependencyMap from './pages/registry/DependencyMap';
import GapDashboard from './pages/registry/GapDashboard';
import PerfDashboard from './pages/PerfDashboard';
import SweepstakesDrawing from './pages/SweepstakesDrawing';
import QACallEvaluation from './pages/QACallEvaluation';
import QADashboard from './pages/QADashboard';
import APLHealthDashboard from './pages/APLHealthDashboard';

// Wraps useNavigate to keep the legacy onNavigate(section) signature used by
// child pages — `setActiveSection('foo')` becomes `navigate('/foo')`. Routes
// the special `registry-detail:<id>` token to `/registry/<id>`.
function useAppNavigate() {
  const navigate = useNavigate();
  return useCallback((section) => {
    if (typeof section !== 'string') return;
    if (section.startsWith('registry-detail:')) {
      navigate(`/registry/${section.slice('registry-detail:'.length)}`);
      return;
    }
    navigate(`/${section}`);
  }, [navigate]);
}

function ServiceDetailRoute() {
  const { serviceId } = useParams();
  const onNavigate = useAppNavigate();
  return <ServiceDetail serviceId={serviceId} onNavigate={onNavigate} />;
}

function AppRoutes() {
  const location = useLocation();
  const onNavigate = useAppNavigate();

  useEffect(() => {
    const screen = location.pathname === '/' ? 'dashboard' : location.pathname.slice(1);
    setCurrentScreenName(screen);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/apl-health" element={<APLHealthDashboard />} />
      <Route path="/tenant-adoption" element={<TenantAdoption />} />
      <Route path="/health-metrics" element={<HealthMetrics />} />
      <Route path="/card-metrics" element={<CardMetrics />} />
      <Route path="/card-transaction-patterns" element={<CardTransactionPatterns />} />
      <Route path="/consumer-queries" element={<ConsumerQueries onNavigate={onNavigate} />} />
      <Route path="/call-center-dashboard" element={<CallCenterDashboard />} />
      <Route path="/call-center-daily-health" element={<DailyCallCenterHealthSummary />} />
      <Route path="/call-center-agent-productivity" element={<AgentProductivityDaily />} />
      <Route path="/call-center-intent-patterns" element={<CallIntentPatterns />} />
      <Route path="/call-center-case-patterns" element={<CasePatternAnalysis />} />
      <Route path="/qa-dashboard" element={<QADashboard />} />
      <Route path="/qa-call-evaluation" element={<QACallEvaluation />} />
      <Route path="/fis-apl-lookup" element={<AplLookup />} />
      <Route path="/fis-merchant-network" element={<MerchantNetworkLookup />} />
      <Route path="/fis-card30" element={<FISCard30 />} />
      <Route path="/fis-card35" element={<FISCard35 />} />
      <Route path="/enrollment-checklist" element={<EnrollmentChecklist />} />
      <Route path="/technology" element={<TechnologyDashboard onNavigate={onNavigate} />} />
      <Route path="/sprint-goals" element={<SprintGoals onNavigate={onNavigate} />} />
      <Route path="/technology-release-readiness" element={<ReleaseReadiness onNavigate={onNavigate} />} />
      <Route path="/technology-sprint-platform" element={<SprintTeamPage teamKey="platform" onNavigate={onNavigate} />} />
      <Route path="/technology-sprint-clientPortal" element={<SprintTeamPage teamKey="clientPortal" onNavigate={onNavigate} />} />
      <Route path="/technology-sprint-analytics" element={<SprintTeamPage teamKey="analytics" onNavigate={onNavigate} />} />
      <Route path="/technology-sprint-serviceCloud" element={<SprintTeamPage teamKey="serviceCloud" onNavigate={onNavigate} />} />
      <Route path="/technology-kanban-maintenanceSupport" element={<KanbanTeamPage teamKey="maintenanceSupport" onNavigate={onNavigate} />} />
      <Route path="/technology-kanban-devops" element={<KanbanTeamPage teamKey="devops" onNavigate={onNavigate} />} />
      <Route path="/technology-kanban-marketingCloud" element={<KanbanTeamPage teamKey="marketingCloud" onNavigate={onNavigate} />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/query" element={<CustomQuery />} />
      <Route path="/tech-support" element={<TechSupport />} />
      <Route path="/reconciliation-report" element={<ReconciliationReport />} />
      <Route path="/log-patterns" element={<LogPatterns />} />
      <Route path="/admin-users" element={<UserManagement />} />
      <Route path="/admin-roles" element={<RoleManagement />} />
      <Route path="/audit-logs" element={<AuditLogs />} />
      <Route path="/live-calls" element={<LiveCalls />} />
      <Route path="/print-ops-health" element={<PrintOpsHealth />} />
      <Route path="/print-ops-file-tracker" element={<PrintOpsFileTracker />} />
      {/* Perf dashboard — S3-hosted HTML SPA, reachable by direct URL only (no sidebar entry) */}
      <Route path="/perf-dashboard" element={<PerfDashboard />} />
      <Route path="/eligibility-check" element={<EligibilityCheck />} />
      <Route path="/eligibility-data" element={<EligibilityData />} />
      <Route path="/agent-ops-eligibility-data" element={<EligibilityData />} />
      <Route path="/file-integrity" element={<FileIntegrity />} />
      <Route path="/prefunding-report" element={<PrefundingReport />} />
      <Route path="/reward-integrity-checks" element={<RewardIntegrityChecks />} />
      <Route path="/master-action-catalog" element={<MasterActionCatalog />} />
      <Route path="/acc-config" element={<AccConfigBuilder />} />
      <Route path="/acc-guided" element={<AccGuidedBuilder />} />
      <Route path="/acc-wizard" element={<AccWizard />} />
      <Route path="/acc-learn" element={<AccLearn onNavigate={onNavigate} />} />
      <Route path="/card-status-history" element={<CardStatusHistory />} />
      {/* ADM-1009 prototype — remove on cleanup / merge decline */}
      <Route path="/card-ordering-audit" element={<CardOrderingAudit />} />
      {/* Consumer Summary under Issue Management reuses ConsumerQueries component */}
      <Route path="/consumer-summary" element={<ConsumerQueries onNavigate={onNavigate} />} />
      <Route path="/recon-rules" element={<ReconRules />} />
      <Route path="/recon-rulesets" element={<ReconRulesets />} />
      <Route path="/recon-processes" element={<ReconProcesses />} />
      <Route path="/recon-dashboard" element={<ReconDashboard />} />
      <Route path="/consumer-financial-transactions" element={<ConsumerFinancialTransactions />} />
      <Route path="/registry" element={<ServiceCatalog onNavigate={onNavigate} />} />
      <Route path="/registry-graph" element={<DependencyMap onNavigate={onNavigate} />} />
      <Route path="/registry-gaps" element={<GapDashboard onNavigate={onNavigate} />} />
      <Route path="/registry/:serviceId" element={<ServiceDetailRoute />} />
      <Route path="/sweepstakes-drawing" element={<SweepstakesDrawing />} />
      <Route path="*" element={<Dashboard />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={getBasePath()}>
      <Layout>
        <AppRoutes />
      </Layout>
    </BrowserRouter>
  );
}
